import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import FileInput from '@/components/FileInput';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { fileToBase64 } from '@/lib/utils';

import {
  type FinancialOverviewInput,
  GET_FINANCIAL_OVERVIEW,
  type GetFinancialOverviewResponse,
  UPDATE_FINANCIAL_OVERVIEW,
  type UpdateFinancialOverviewResponse,
  UPLOAD_FINANCIAL_IMAGE,
  type UploadFinancialImageResponse
} from './graphql';

const FinancialOverviewTab = ({ dealId }: { dealId: string }) => {
  const { data, error, loading, refetch } = useQuery<
    GetFinancialOverviewResponse,
    { id: string }
  >(GET_FINANCIAL_OVERVIEW, { variables: { id: dealId } });

  const [updateFinancialOverview, { loading: isUpdating }] = useMutation<
    UpdateFinancialOverviewResponse,
    { id: string; values: FinancialOverviewInput }
  >(UPDATE_FINANCIAL_OVERVIEW);

  const [uploadFinancialImage, { loading: isUploading }] = useMutation<
    UploadFinancialImageResponse,
    { financialImage: string; financialImageFilename?: string; id: string }
  >(UPLOAD_FINANCIAL_IMAGE);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [commentaryImageLayout, setCommentaryImageLayout] =
    useState<string>('');
  const financialAnalysisFieldRef = useRef<Editor>(null);
  const financialCommentaryFieldRef = useRef<Editor>(null);

  const financialOverview = data?.node?.financialOverviewId;

  useEffect(() => {
    const layout = financialOverview?.financialCommentaryImageLayout ?? '';
    setCommentaryImageLayout(layout);
  }, [financialOverview?.financialCommentaryImageLayout]);

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!financialOverview) {
      toast.error('No financial overview found');
      return;
    }

    if (
      !financialAnalysisFieldRef.current ||
      !financialCommentaryFieldRef.current
    ) {
      toast.error('Unable to read financial overview fields');
      return;
    }

    const formData = new FormData(event.currentTarget);
    const financialImageFile = formData.get('financial-image-field');
    const projectedFinancialModelFile = formData.get(
      'projected-financial-model-field'
    );
    const projectedLiquidityFile = formData.get('projected-liquidity-field');

    try {
      const values: FinancialOverviewInput = {
        financialAnalysis: financialAnalysisFieldRef.current.isEmpty
          ? undefined
          : financialAnalysisFieldRef.current.getHTML(),
        financialCommentary: financialCommentaryFieldRef.current.isEmpty
          ? undefined
          : financialCommentaryFieldRef.current.getHTML(),
        ...(commentaryImageLayout
          ? { financialCommentaryImageLayout: commentaryImageLayout }
          : {}),
        financialImage: null
      };

      if (
        projectedFinancialModelFile instanceof File &&
        projectedFinancialModelFile.size > 0
      ) {
        values.projectedFinancialModel = await fileToBase64(
          projectedFinancialModelFile
        );
      } else {
        values.projectedFinancialModel = null;
      }

      if (
        projectedLiquidityFile instanceof File &&
        projectedLiquidityFile.size > 0
      ) {
        values.projectedLiquidityAndCreditMetrics = await fileToBase64(
          projectedLiquidityFile
        );
      } else {
        values.projectedLiquidityAndCreditMetrics = null;
      }

      const response = await updateFinancialOverview({
        variables: {
          id: financialOverview.id,
          values
        }
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data?.updateFinancialOverview?.ok) {
        throw new Error('Unable to save financial overview');
      }

      if (financialImageFile instanceof File && financialImageFile.size > 0) {
        const imageBase64 = await fileToBase64(financialImageFile);
        const uploadResponse = await uploadFinancialImage({
          variables: {
            financialImage: imageBase64,
            financialImageFilename: financialImageFile.name,
            id: financialOverview.id
          }
        });

        if (uploadResponse.errors && uploadResponse.errors.length > 0) {
          throw new Error(uploadResponse.errors[0].message);
        }

        if (!uploadResponse.data?.uploadFinancialImage?.ok) {
          throw new Error('Unable to upload financial image');
        }
      }

      await refetch();
      toast.success('Financial overview updated successfully');
      setIsDialogOpen(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to save financial overview';
      toast.error(errorMessage);
    }
  };

  if (loading && !data) {
    return (
      <div className="grid h-64 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid h-64 place-content-center text-red-500">
        Error loading financial overview: {error.message}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2.5 pt-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Financial Overview</h2>
          <EditButton
            onClick={() => {
              setIsDialogOpen(true);
            }}
          />
        </div>
        <div className="bg-card @container rounded-md border p-5">
          <div className="space-y-5">
            <div className="border-b pb-2.5">
              <h3 className="text-base font-medium">
                Financial Analysis & Projections
              </h3>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Financial Analysis</div>
              <RichTextFieldReadOnly
                html={financialOverview?.financialAnalysis ?? ''}
              />
            </div>
            <div className="grid gap-2.5 @md:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  Projected Financial Model
                </div>
                <FileInput
                  accept="image/*"
                  data={financialOverview?.projectedFinancialModel}
                  readOnly
                />
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  Projected Liquidity and Credit Metrics
                </div>
                <FileInput
                  accept="image/*"
                  data={financialOverview?.projectedLiquidityAndCreditMetrics}
                  readOnly
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Financial Commentary</div>
              <RichTextFieldReadOnly
                html={financialOverview?.financialCommentary ?? ''}
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium">Financial Image</div>
              <FileInput
                accept="image/*"
                data={financialOverview?.financialImage}
                readOnly
              />
            </div>
            {financialOverview?.financialCommentaryImageLayout ? (
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  Commentary And Image Layout
                </div>
                <TruncatedTextTooltip className="text-muted-foreground text-sm capitalize">
                  {financialOverview.financialCommentaryImageLayout}
                </TruncatedTextTooltip>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Dialog onOpenChange={setIsDialogOpen} open={isDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Financial Overview
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id="financial-overview-form"
              noValidate
              onSubmit={event => void handleSave(event)}
            >
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="financial-analysis-field">
                    Financial Analysis
                  </FieldLabel>
                  <RichTextField
                    defaultValue={financialOverview?.financialAnalysis ?? ''}
                    id="financial-analysis-field"
                    ref={financialAnalysisFieldRef}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-x-2.5">
                  <Field>
                    <FieldLabel htmlFor="projected-financial-model-field">
                      Projected Financial Model
                    </FieldLabel>
                    <FileInput
                      accept="image/*"
                      data={financialOverview?.projectedFinancialModel}
                      id="projected-financial-model-field"
                      name="projected-financial-model-field"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="projected-liquidity-field">
                      Projected Liquidity and Credit Metrics
                    </FieldLabel>
                    <FileInput
                      accept="image/*"
                      data={
                        financialOverview?.projectedLiquidityAndCreditMetrics
                      }
                      id="projected-liquidity-field"
                      name="projected-liquidity-field"
                    />
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="financial-commentary-field">
                    Financial Commentary
                  </FieldLabel>
                  <RichTextField
                    defaultValue={financialOverview?.financialCommentary ?? ''}
                    id="financial-commentary-field"
                    ref={financialCommentaryFieldRef}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="financial-image-field">
                    Financial Image
                  </FieldLabel>
                  <FileInput
                    accept="image/*"
                    data={financialOverview?.financialImage}
                    id="financial-image-field"
                    name="financial-image-field"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="commentary-image-layout">
                    Commentary And Image Layout
                  </FieldLabel>
                  <Select
                    onValueChange={setCommentaryImageLayout}
                    value={commentaryImageLayout || undefined}
                  >
                    <SelectTrigger
                      className="w-full"
                      id="commentary-image-layout"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vertical">Vertical</SelectItem>
                      <SelectItem value="horizontal">Horizontal</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </FieldGroup>
            </form>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={isUpdating || isUploading}
              form="financial-overview-form"
              type="submit"
            >
              {(isUpdating || isUploading) && <Spinner />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinancialOverviewTab;
